import React, { useState } from "react";
import { Section } from "../../Global/Section/Section";
import VidstackPlayer from "../../Global/Video/VidstackPlayer";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { HeaderVideosType } from "@/types/PageType";
import { Post } from "@/wp/types/client";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { customSanitize } from "@/utils/customSanitize";
import styles from "./VideoHeader.module.scss";
import { ContentBlockerWrapper } from "@aleksundshantu.com/aunds-cookie-plugin-react";

interface VideoHeaderProps {
  post: Post<"videos">;
}

function VideoHeader({ post }: VideoHeaderProps) {
  const { getLabel } = useGlobalContext();
  const videoData = post.acf.header_videos as HeaderVideosType;
  const { transcript, audio_description_video_desktop } = videoData;
  const [activeVideo, setActiveVideo] = useState<
    "original" | "audio-description"
  >("original");

  function handleTabChange(newTab: string) {
    setActiveVideo(newTab as "original" | "audio-description");
  }
  const originalVideo = (
    <VidstackPlayer
      autoplay={false}
      loop={false}
      muted={false}
      video={videoData}
      video_decorative={false}
      singlePage={true}
      sizes="100vw, (min-width:768px) 80vw, (min-width: 1024px) 67vw"
    />
  );
  return (
    <header className={`${styles.header} has-body-bg`}>
      <h1 className="h1-pages has-text-centered pt-6">{post.title.rendered}</h1>
      <Section columnsClassName="is-centered">
        <div className="column is-10-tablet is-8-desktop is-8-widescreen">
          <div>
            <ul className="is-flex">
              <li
                className={`${styles.tab} ${
                  activeVideo === "original" ? styles.active : ""
                }`}
              >
                <button
                  role="button"
                  tabIndex={0}
                  onClick={() => handleTabChange("original")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleTabChange("original");
                    }
                  }}
                  aria-label={`${getLabel(
                    "video_single_page_original_video",
                    "Originalvideo",
                  )} Button`}
                  className="button-reset"
                >
                  {getLabel(
                    "video_single_page_original_video",
                    "Originalvideo",
                  )}
                </button>
              </li>
              {audio_description_video_desktop && (
                <li
                  className={`${styles.tab} ${
                    activeVideo === "audio-description" ? styles.active : ""
                  }`}
                >
                  <button
                    className="button-reset"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleTabChange("audio-description")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleTabChange("audio-description");
                      }
                    }}
                    aria-label={`${getLabel(
                      "video_single_page_audio_description_video",
                      "Video mit Audiobeschreibung",
                    )} Button`}
                  >
                    {getLabel(
                      "video_single_page_audio_description_video",
                      "Video mit Audiobeschreibung",
                    )}
                  </button>
                </li>
              )}
            </ul>
          </div>

          {post && (
            <>
              {/* Original Video */}
              {activeVideo === "original" && (
                <>
                  {videoData.video_type !== "upload" ? (
                    <ContentBlockerWrapper
                      platformName={videoData.video_type || "unknown"}
                      wpApiUrl={process.env.WP_API_URL!}
                      extraClassName={`${styles["video-blocker"]}`}
                    >
                      {originalVideo}
                    </ContentBlockerWrapper>
                  ) : (
                    originalVideo
                  )}
                </>
              )}
              {/* Audio Description Video */}
              {audio_description_video_desktop &&
                activeVideo === "audio-description" && (
                  <VidstackPlayer
                    autoplay={false}
                    loop={false}
                    muted={false}
                    video={videoData}
                    video_audio_description={audio_description_video_desktop}
                    video_decorative={false}
                    singlePage={true}
                    sizes="100vw, (min-width:768px) 80vw, (min-width: 1024px) 67vw"
                  />
                )}
            </>
          )}

          {/* Transcript text */}
          <section>
            {transcript.text && (
              <>
                <h2 className="h1-pages has-text-centered pt-6">
                  {getLabel("transcript", "Transkription")}
                </h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html: customSanitize(transcript.text),
                  }}
                ></div>
              </>
            )}

            {transcript.textfile && (
              <ExtendedLink
                extraClass="mx-auto"
                btnType="primary"
                extendedLink={{
                  type: "download",
                  download: {
                    link_target: transcript.textfile,
                    link_text: getLabel(
                      "download_transcript",
                      "Transkript herunterladen",
                    ),
                  },
                  link: {
                    url: "",
                    target: "",
                    title: "",
                    aria_label: "",
                  },
                  email: {
                    link_target: "",
                    link_text: "",
                  },
                  pop_up: {
                    link_text: "",
                    link_aria_label: "",
                  },
                }}
              ></ExtendedLink>
            )}
          </section>
        </div>
      </Section>
    </header>
  );
}

export default VideoHeader;
